import { Check, X, Circle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  ProvisionStep,
  StepHistoryEntry,
} from "@/application/provisioning/types";
import { STEP_LABELS } from "@/application/provisioning/types";

const STEP_ORDER: ProvisionStep[] = [
  "cognito_user",
  "cognito_group",
  "user_profile",
  "business_profile",
  "stripe_account",
  "stripe_link",
  "welcome_email",
];

interface StepTimelineProps {
  currentStep: ProvisionStep;
  failedStep: string | null;
  stepHistory: StepHistoryEntry[];
  status: string;
}

export function StepTimeline({
  currentStep,
  failedStep,
  stepHistory,
  status,
}: StepTimelineProps) {
  const historyMap = new Map(stepHistory.map((h) => [h.step, h]));

  return (
    <div className="space-y-1">
      {STEP_ORDER.map((step) => {
        const history = historyMap.get(step);
        const isFailed = step === failedStep;
        const isCompleted = history?.status === "completed";
        const isCurrent = step === currentStep && status === "in_progress";
        const isPending = !isCompleted && !isFailed && !isCurrent;

        return (
          <div
            key={step}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2",
              isFailed && "bg-red-50",
            )}
          >
            {isCompleted && (
              <Check className="h-4 w-4 shrink-0 text-green-600" />
            )}
            {isFailed && <X className="h-4 w-4 shrink-0 text-red-600" />}
            {isCurrent && (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-600" />
            )}
            {isPending && (
              <Circle className="text-subtle-foreground h-4 w-4 shrink-0" />
            )}
            <span
              className={cn(
                "flex-1 text-sm",
                isFailed && "font-medium text-red-700",
                isPending && "text-subtle-foreground",
                isCompleted && "text-foreground",
                isCurrent && "font-medium text-blue-700",
              )}
            >
              {/* eslint-disable-next-line security/detect-object-injection -- typed enum key */}
              {STEP_LABELS[step]}
            </span>
            {isCompleted && history?.timestamp && (
              <span className="text-subtle-foreground text-xs">Done</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
